import {
  Component,
  OnInit,
  signal,
  computed,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';

import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import {
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  StockChangeService,
  StockChangeSearchParams,
} from './stock-change.service';
import {
  StockChangeRecord,
  STOCK_CHANGE_SEARCH_CONFIG,
  STOCK_CHANGE_TABLE_CONFIG,
  STOCK_CHANGE_DEFAULT_CONFIG,
  STOCK_CHANGE_MESSAGES,
  STOCK_CHANGE_DEFAULT_COLUMNS_ORDER,
  STOCK_CHANGE_FLEXIBLE_COLUMNS_ORDER,
  STOCK_CHANGE_REFERENCE_COLUMNS_ORDER,
} from './stock-change.config';
import { BASE_PATH, ChangeLogControllerService } from '@app/api';
import { environment } from '@environments/environment';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';

@Component({
  selector: 'app-stock-change',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TooltipModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    ChangeLogControllerService,
    StockChangeService,
  ],
  templateUrl: './stock-change.component.html',
  styleUrl: './stock-change.component.css',
})
export class StockChangeComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private router = inject(Router);
  private messageService = inject(MessageService);
  private stockChangeService = inject(StockChangeService);
  private destroy$ = new Subject<void>();

  // Search form configuration
  searchFormat = STOCK_CHANGE_SEARCH_CONFIG;

  // Advanced table column sets (for Cấu hình cột)
  defaultColumns = computed((): TableColumn[] => {
    const byProp = (prop: string) =>
      STOCK_CHANGE_TABLE_CONFIG.find((c) => c.prop === prop);
    return STOCK_CHANGE_DEFAULT_COLUMNS_ORDER.map((prop) =>
      byProp(prop),
    ).filter((c): c is TableColumn => !!c);
  });

  flexibleColumns = computed((): TableColumn[] => {
    const byProp = (prop: string) =>
      STOCK_CHANGE_TABLE_CONFIG.find((c) => c.prop === prop);
    return STOCK_CHANGE_FLEXIBLE_COLUMNS_ORDER.map((prop) =>
      byProp(prop),
    ).filter((c): c is TableColumn => !!c);
  });

  referenceColumnOrder = computed((): TableColumn[] => {
    const byProp = (prop: string) =>
      STOCK_CHANGE_TABLE_CONFIG.find((c) => c.prop === prop);
    return STOCK_CHANGE_REFERENCE_COLUMNS_ORDER.map((prop) =>
      byProp(prop),
    ).filter((c): c is TableColumn => !!c);
  });

  // Data signals
  dataSource = signal<StockChangeRecord[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  first = signal(0);
  rows = signal<number>(STOCK_CHANGE_DEFAULT_CONFIG.PAGE_SIZE);

  // Flag to prevent double API calls
  private isSearching = false;

  ngOnInit(): void {
    this.loadData();
    this.loadOptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    if (this.isSearching) return; // Prevent double calls

    this.isSearching = true;
    this.first.set(0);

    // Clean up search criteria - remove empty values
    const cleanCriteria = this.cleanSearchCriteria(searchData);
    this.loadData(cleanCriteria);
  }

  onReset(): void {
    this.first.set(0);
    this.loadData();
  }

  onPageChange(event: PaginationEvent): void {
    const page = event.page ?? 0;
    const rowsPerPage = event.rows ?? this.rows();
    this.first.set(page * rowsPerPage);
    this.rows.set(rowsPerPage);
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  onViewDetails(record: StockChangeRecord): void {
    this.router.navigate(['/stock/change/view', record.id]);
  }

  // Refresh data
  refreshData(): void {
    this.first.set(0);
    this.loadData();
    this.messageService.add({
      severity: 'info',
      summary: 'Refresh',
      detail: 'Data refreshed successfully',
    });
  }

  private loadData(searchParams?: SearchFormData): void {
    this.loading.set(true);

    const params: StockChangeSearchParams = {
      page: Math.floor(this.first() / this.rows()),
      size: this.rows(),
      ...searchParams,
    };

    this.stockChangeService
      .getChangeRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
          this.isSearching = false;
        },
        error: (error) => {
          console.error('Error loading stock change data:', error);
          this.loading.set(false);
          this.isSearching = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: STOCK_CHANGE_MESSAGES.ERROR.DATA_LOAD_FAILED,
          });

          // Set empty data on error
          this.dataSource.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  // Clean up search criteria by removing empty/null values
  private cleanSearchCriteria(criteria: any): any {
    const cleanCriteria: any = {};

    Object.keys(criteria).forEach((key) => {
      const value = criteria[key];

      // Keep non-empty values
      if (value !== null && value !== undefined && value !== '') {
        // For arrays, keep if they have items
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanCriteria[key] = value;
          }
        } else {
          cleanCriteria[key] = value;
        }
      }
    });

    return cleanCriteria;
  }

  private loadOptions(): void {
    // Load category tree options
    this.stockChangeService
      .getCategoryOptions({ size: 20 })
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => {
        this.searchFormat = this.searchFormat.map((field) =>
          field.key === 'categoryId' ? { ...field, options: options } : field,
        );
      });
  }
}
